import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { LogIn, Globe, AlertTriangle, Package } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

const TYPEWRITER_PHRASES = [
  'ادارة شحناتك بذكاء...',
  'Smart Logistics Solutions...',
  '10110010 01001101 11010...',
  'تتبع فوري لكل طرد...',
  'Track Every Shipment...',
  'if (delivered) success();',
  'حلول لوجستية متطورة...',
  'Fast. Accurate. Reliable.',
  '01001000 10101110 010...',
  'سرعة، دقة، موثوقية...',
];

const useTypewriter = (phrases: string[], typingSpeed = 80, deletingSpeed = 40, pauseTime = 2000) => {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = phrases[phraseIndex];

    if (!isDeleting && charIndex === current.length) {
      const timeout = setTimeout(() => setIsDeleting(true), pauseTime);
      return () => clearTimeout(timeout);
    }

    if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
      return;
    }

    const speed = isDeleting ? deletingSpeed : typingSpeed;
    const timeout = setTimeout(() => {
      setCharIndex((prev) => prev + (isDeleting ? -1 : 1));
    }, speed);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, phraseIndex, phrases, typingSpeed, deletingSpeed, pauseTime]);

  return phrases[phraseIndex].slice(0, charIndex);
};

const REMEMBER_KEY = 'auth-remember-username';

const Auth = () => {
  const { t, dir, lang, setLang } = useLanguage();
  const { user, loading, signIn } = useAuth();
  const { company, loading: tenantLoading, subdomain, isSuperAdminDomain } = useTenant();
  const displayText = useTypewriter(TYPEWRITER_PHRASES);

  const [username, setUsername] = useState(() => localStorage.getItem(REMEMBER_KEY) || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(REMEMBER_KEY));

  // One-time cleanup of legacy plaintext password from localStorage
  useEffect(() => {
    localStorage.removeItem('auth-remember-password');
  }, []);
  const [submitting, setSubmitting] = useState(false);

  const isCompanySubdomain = subdomain && subdomain !== 'default' && !isSuperAdminDomain;
  const companyNotFound = isCompanySubdomain && !tenantLoading && !company;

  // Branding options
  const bo = (company as any)?.branding_options || {};
  const showEffects = bo.login_effects_enabled !== false;
  const loginBgImage = bo.login_bg_image as string | undefined;
  const loginBgColor = bo.login_bg_color as string | undefined;

  if (loading || tenantLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (user) return <Navigate to="/" replace />;

  if (companyNotFound) {
    return (
      <div className="relative flex min-h-screen items-center justify-center p-4 overflow-hidden" dir={dir}>
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, hsl(var(--primary) / 0.95) 0%, hsl(var(--primary) / 0.8) 50%, hsl(var(--primary)) 100%)` }} />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 w-full max-w-sm">
          <Card className="border-none shadow-2xl bg-black/50 backdrop-blur-2xl text-white">
            <CardContent className="pt-8 pb-8 text-center space-y-4">
              <AlertTriangle className="h-16 w-16 text-yellow-400 mx-auto" />
              <h2 className="text-xl font-bold text-white">{lang === 'ar' ? 'شركة غير موجودة' : 'Company Not Found'}</h2>
              <p className="text-slate-300/80 text-sm">
                {lang === 'ar'
                  ? `النطاق "${subdomain}.deliveryjo.com" غير مرتبط بأي شركة مفعلة.`
                  : `The domain "${subdomain}.deliveryjo.com" is not linked to any active company.`}
              </p>
              <p className="text-slate-400/60 text-xs">
                {lang === 'ar' ? 'تواصل مع مزود الخدمة للمساعدة.' : 'Contact your service provider for help.'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleLogin = async () => {
    if (!username.trim() || !password) {
      toast.error(t('required'));
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      toast.error('لا يوجد اتصال بالإنترنت — لا يمكن تسجيل الدخول الآن');
      return;
    }
    setSubmitting(true);

    if (rememberMe) {
      localStorage.setItem(REMEMBER_KEY, username.trim());
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }

    const email = `${username.toLowerCase().trim()}@app.local`;
    const { error } = await signIn(email, password);
    setSubmitting(false);
    if (error) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        toast.error('لا يوجد اتصال بالإنترنت — لا يمكن تسجيل الدخول الآن');
      } else {
        toast.error(t('loginError'));
      }
    }
  };

  // Resolve login logo
  const loginLogo = company?.login_logo_url || company?.logo_url || null;
  const companyName = company?.name;

  return (
    <div
      className="relative flex min-h-screen items-center justify-center p-4 overflow-hidden"
      dir={dir}
    >
      {/* Background layers */}
      {loginBgImage ? (
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${loginBgImage})` }} />
      ) : loginBgColor ? (
        <div className="absolute inset-0" style={{ background: loginBgColor }} />
      ) : (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, hsl(var(--primary) / 0.95) 0%, hsl(var(--primary) / 0.8) 50%, hsl(var(--primary)) 100%)` }} />
      )}
      <div className="absolute inset-0 bg-black/70" />

      {/* Effects - conditionally rendered */}
      {showEffects && (
        <>
          <div className="futuristic-grid" />
          <div className="futuristic-scanline" />
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="futuristic-particle"
              style={{
                left: `${10 + i * 12}%`,
                top: `${15 + (i % 3) * 30}%`,
                width: `${3 + (i % 3) * 2}px`,
                height: `${3 + (i % 3) * 2}px`,
                animationDelay: `${i * 0.8}s`,
                animationDuration: `${6 + (i % 4) * 2}s`,
              }}
            />
          ))}
          <div className="futuristic-ring futuristic-ring-1" />
          <div className="futuristic-ring futuristic-ring-2" />
          <div className="futuristic-ring futuristic-ring-3" />
        </>
      )}

      <div className="relative z-10 w-full max-w-sm futuristic-card-wrapper">
        <Card className="border-none shadow-2xl animate-scale-in bg-black/50 backdrop-blur-2xl text-white futuristic-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              {loginLogo ? (
                <img
                  src={loginLogo}
                  alt={companyName || 'Logo'}
                  className="h-[160px] w-[160px] object-contain rounded-xl"
                  fetchPriority="high"
                  width={160}
                  height={160}
                />
              ) : (
                <div className="flex items-center justify-center h-[120px] w-[120px]">
                  <Package className="h-20 w-20 text-primary/80" />
                </div>
              )}
            </div>
            {companyName && (
              <h1 className="text-2xl font-bold text-white mb-1">{companyName}</h1>
            )}
            {!company && (
              <div className="h-10 flex items-center justify-center" dir="rtl">
                <span className="text-2xl text-sky-300/80 font-light tracking-wide">
                  {displayText}
                </span>
                <span className="typewriter-cursor" />
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="auth-username" className="text-slate-200/80">{t('username')}</Label>
              <Input
                id="auth-username"
                dir="ltr"
                value={username}
                onChange={e => setUsername(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                autoComplete="username"
                className="futuristic-input focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="auth-password" className="text-slate-200/80">{t('password')}</Label>
              <Input
                id="auth-password"
                type="password"
                dir="ltr"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                autoComplete="current-password"
                className="futuristic-input focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(!!checked)}
              />
              <Label htmlFor="remember-me" className="cursor-pointer text-sm font-normal text-slate-200/70">
                {t('rememberMe')}
              </Label>
            </div>

            <Button className="w-full gap-2 futuristic-btn" onClick={handleLogin} disabled={submitting}>
              <LogIn className="h-4 w-4" />
              {submitting ? '...' : t('login')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="fixed bottom-4 left-0 right-0 z-50 flex items-center justify-between px-4">
        <button
          onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
          className="flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-md px-3 py-1.5 text-xs text-slate-200/80 hover:bg-white/20 transition-colors"
        >
          <Globe className="h-3.5 w-3.5" />
          {lang === 'ar' ? 'EN' : 'عربي'}
        </button>

        <span className="text-[11px] text-slate-300/50">
          by: <span className="font-semibold text-slate-300/70">Desite Ai</span>
        </span>

        <button
          onClick={() => window.open('mailto:contact@desite.ai', '_blank')}
          className="flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-md px-3 py-1.5 text-xs text-slate-200/80 hover:bg-white/20 transition-colors"
        >
          {t('contactUs')}
        </button>
      </div>
    </div>
  );
};

export default Auth;
